import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import {
  MenuAddMunicipality,
  FiltersMunicipality,
} from 'system/geographic/components/municipality/list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useRef, useEffect } from 'react';
import { Municipality } from 'shell/models/municipality';
import { FiltersFormMunicipality, MunicipalityClient } from './types';
import { useFormik } from 'formik';
import { transformDataset, transformResponseMunicipalities } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesMunicipality } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonMunicipalityCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import CtaMenuMunicipalities from 'system/geographic/components/municipality/list/cta';
import { authsMunicipality, headCells, validationSchemaMunicipalityFilter } from './constants';
import { Typography } from '@mui/material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';

const MunicipalityListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<MunicipalityClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<MunicipalityClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormMunicipality>();

  const totalCountRef = useRef<number | null>(null);

  const [municipalities, getMunicipalityList] = useHttpFetch<
    HttpResponseList<Municipality>,
    Array<MunicipalityClient>
  >(`${__MUNICIPALITY_API_URL__}/geo/comune`, {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
    transformer: (response) => {
      totalCountRef.current = response.data.payload.pageInfo?.totalCount || null;
      return transformResponseMunicipalities(response);
    },
    onBootstrap: false,
  });

  const municipalitiesData = municipalities.data as Array<MunicipalityClient>;

  const deleteMunicipality = useHttpClient<HttpResponse<Municipality>>(
    `${__MUNICIPALITY_API_URL__}/geo/comune`,
    {
      options: {
        method: 'DELETE',
        loading: true,
        errorCatching: true,
      },
    }
  );

  const onTrashPress = useCallback(
    (cell): Promise<MunicipalityClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<MunicipalityClient>;
    },
    [setDeleteAlertModal]
  );

  const auths = useActionAuthorization(authsMunicipality);

  const municipalityList: Array<MunicipalityClient> = useMemo(() => {
    return municipalitiesData?.map((cell) => ({
      ...cell,
      action: (
        <CtaMenuMunicipalities
          active={{
            ...auths,
            delete: auths.delete || !cell.isVoid,
            edit: auths.edit || !cell.isVoid,
          }}
          cell={cell}
          hidden={{ delete: true, ended: !cell.isEnded }}
          onTrashPress={onTrashPress}
        />
      ),
    }));
  }, [municipalitiesData, onTrashPress, auths]);

  const onSubmit = useCallback(
    (formValues: FiltersFormMunicipality) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterMunicipalityForm.setFieldValue('page', 0);
      getMunicipalityList({ params: {}, paramsSerializer: () => queryString });
    },
    [getMunicipalityList]
  );

  const filterMunicipalityForm = useFormik({
    initialValues: initialValuesMunicipality,
    validationSchema: validationSchemaMunicipalityFilter,
    onSubmit,
  });

  useEffect(() => {
    const queryString = transformDataset(filterTable.values || initialValuesMunicipality);
    getMunicipalityList({ params: {}, paramsSerializer: () => queryString });
    filterMunicipalityForm.setValues(filterTable.values || initialValuesMunicipality);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterMunicipalityForm.values);
  }, [filterMunicipalityForm.values]);

  const count: number = useMemo(() => {
    return totalCountRef.current || 0;
  }, [totalCountRef.current]);

  const navigate = useNavigate();

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterMunicipalityForm.values, orders };
      const queryString = transformDataset(newDataset);
      return (
        getMunicipalityList({ params: {}, paramsSerializer: () => queryString }) as ResponsePromise<
          HttpResponseList<Municipality>,
          Array<MunicipalityClient>
        >
      ).then(() => {
        filterMunicipalityForm.setFieldValue('orders', orders);
      });
    },
    [filterMunicipalityForm.setFieldValue, getMunicipalityList, filterMunicipalityForm.values]
  );

  const handleChangePage = useCallback(
    (page) => {
      const newDataset = { ...filterMunicipalityForm.values, page: page || 0 };

      const queryString = transformDataset(newDataset);
      return (
        getMunicipalityList({ params: {}, paramsSerializer: () => queryString }) as ResponsePromise<
          HttpResponseList<Municipality>,
          Array<MunicipalityClient>
        >
      ).then(() => {
        filterMunicipalityForm.setFieldValue('page', page);
      });
    },
    [filterMunicipalityForm.values, getMunicipalityList, filterMunicipalityForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterMunicipalityForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      const queryString = transformDataset(newDataset);
      return (
        getMunicipalityList({ params: {}, paramsSerializer: () => queryString }) as ResponsePromise<
          HttpResponseList<Municipality>,
          Array<MunicipalityClient>
        >
      ).then(() => {
        filterMunicipalityForm.setValues(newDataset);
      });
    },
    [filterMunicipalityForm.setValues, filterMunicipalityForm.values, getMunicipalityList]
  );

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    (
      deleteMunicipality({ url: `/${deleteAlertModal.element?.code}` }) as ResponsePromise<void>
    ).then(() => {
      setDeleteAlertModal({ open: false, element: null });
      setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
    });
  }, [setDeleteAlertModal, setDeleteSuccessModal, deleteMunicipality, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onCloseOnOkDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset(filterMunicipalityForm.values);
    (
      getMunicipalityList({ params: {}, paramsSerializer: () => queryString }) as ResponsePromise<
        HttpResponseList<Municipality>,
        Array<MunicipalityClient>
      >
    ).then(() => setDeleteSuccessModal({ open: false, element: null }));
  }, [getMunicipalityList, setDeleteSuccessModal, filterMunicipalityForm.values]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonMunicipalityCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova regione"
        addBtnLabel="Aggiungi nuova regione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/geografiche/italia/regione/nuovo');
        }}
        title="Gestione Comune"
        authorizationTooltipContent={
          <div className="pmi-d-inline-block">
            <MenuAddMunicipality />
          </div>
        }
      />
      <div className="pmi-my-3">
        <FiltersMunicipality filterMunicipalityForm={filterMunicipalityForm} />
      </div>
      {municipalitiesData ? (
        municipalityList?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterMunicipalityForm.values.orders}
            activeActions={auths}
            page={filterMunicipalityForm.values.page}
            rowsPerPage={filterMunicipalityForm.values.rowsPerPage}
            body={municipalityList}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Comuni"
            isServer
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione fisica Comune"
        message={`Confermare la cancellazione del comune ${deleteAlertModal.element?.name || ''}?`}
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione fisica Comune"
        message={`La cancellazione del comune ${
          deleteSuccessModal.element?.name || ''
        } è avvenuta con successo`}
        onClose={onCloseOnOkDeleteSuccessModal}
        onOk={onCloseOnOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

MunicipalityListPage.displayName = 'Municipality List page component';

export default MunicipalityListPage;
